/* @generated */

// ../src/motion/motion-component.tsx
import * as React from "react";
import { Transition } from "react-transition-group";

// ../src/_global/forceReflow.ts
var forceReflow = (node) => node && node.scrollTop;

// ../src/motion/motion-component.tsx
var MotionEasingValue = {
  enter: "cubic-bezier(0, 0.6, 0, 1)",
  leave: "cubic-bezier(1, 0, 1, 0.5)",
  transition: "cubic-bezier(0.66, 0, 0, 1)"
};
var MOTION_DURATION_125 = 125;
var MOTION_DURATION_333 = 333;
var MOTION_DURATION_708 = 708;
var MOTION_DURATION_1000 = 1e3;
var MOTION_DURATION_DISABLED = 0;
var MOTION_DURATION_NON_MOTION = MOTION_DURATION_125;
var MOTION_DURATION_MICRO = MOTION_DURATION_333;
var MOTION_DURATION_MICRO_FAST = MOTION_DURATION_125;
var MOTION_DURATION_SURFACE = MOTION_DURATION_333;
var MOTION_DURATION_SURFACE_SLOW = MOTION_DURATION_708;
var getOpacityProps = (transitions) => {
  const props = transitions.filter(({ property }) => property === "opacity");
  return props.length > 0 ? props[props.length - 1].value : 1;
};
var getTransitionPositionProps = (transitions) => {
  const translationValue = [0, 0];
  transitions.filter(
    ({ property }) => property === "up" || property === "down" || property === "left" || property === "right"
  ).forEach(({ property, value }) => {
    if (property === "up") {
      translationValue[1] += -1 * value;
    } else if (property === "down") {
      translationValue[1] += value;
    } else if (property === "left") {
      translationValue[0] += -1 * value;
    } else if (property === "right") {
      translationValue[0] += value;
    }
  });
  return (
    // append `px`
    `${translationValue[0]}px, ${translationValue[1]}px`
  );
};
var Motion = ({
  children,
  style,
  transitionIn,
  transitionOut,
  onEnter,
  _disableMotionDuration = false,
  ...rest
}) => {
  const inDuration = _disableMotionDuration ? MOTION_DURATION_DISABLED : transitionIn.duration;
  const outDuration = _disableMotionDuration ? MOTION_DURATION_DISABLED : transitionOut.duration;
  const timeout = {
    enter: inDuration,
    exit: outDuration
  };
  const transitionStyle = {
    entering: {
      transitionDuration: `${inDuration}ms`,
      // apply opacity
      opacity: getOpacityProps(transitionIn.transitions),
      // apply transform positions
      transform: `translate(${getTransitionPositionProps(transitionIn.transitions)})`
    },
    entered: {
      // apply opacity
      opacity: getOpacityProps(transitionIn.transitions),
      // apply transform positions
      transform: `translate(${getTransitionPositionProps(transitionIn.transitions)})`
    },
    exiting: {
      transitionDuration: `${outDuration}ms`,
      // apply opacity
      opacity: getOpacityProps(transitionOut.transitions),
      // apply transform positions
      transform: `translate(${getTransitionPositionProps(transitionOut.transitions)})`
    },
    exited: {
      // apply opacity
      opacity: getOpacityProps(transitionOut.transitions),
      // apply transform positions
      transform: `translate(${getTransitionPositionProps(transitionOut.transitions)})`
    }
  };
  const transitionEasing = {
    entering: MotionEasingValue[transitionIn.easing],
    entered: MotionEasingValue[transitionIn.easing],
    exiting: MotionEasingValue[transitionOut.easing],
    exited: MotionEasingValue[transitionOut.easing]
  };
  const onEnterWrapper = typeof onEnter === "function" ? (node, isAppearing) => {
    forceReflow(node);
    onEnter(node, isAppearing);
  } : forceReflow;
  return /* @__PURE__ */ React.createElement(Transition, { timeout, ...rest, onEnter: onEnterWrapper }, (state) => children({
    state,
    style: {
      ...style,
      transitionDuration: transitionStyle[state].transitionDuration,
      transitionTimingFunction: transitionEasing[state],
      ...transitionStyle[state]
    }
  }));
};

// ../src/motion/useMotion.ts
import * as React3 from "react";
import { usePrefersReducedMotion } from "@dropbox/dig-foundations";
import { animate } from "motion/mini";

// ../src/motion/constants.ts
var SKIP_ANIMATIONS = "document" in globalThis === false || "window" in globalThis === false;

// ../src/motion/useMotion.ts
import { useIsomorphicLayoutEffect } from "@react-hookz/web";

// ../src/utils.ts
import * as React2 from "react";
var weakInvariant = (condition, message, error) => {
  if (!condition) {
    console.error(`dig-components:`, message, error);
  }
};

// ../src/motion/useMotion.ts
var EASE_ENTER = [0, 0.6, 0, 1];
var EASE_LEAVE = [1, 0, 1, 0.5];
var EASE_TRANSITION = [0.66, 0, 0, 1];
function useMotion(args) {
  const {
    elementRef,
    states,
    currentState,
    shouldReduceMotion = true,
    onTransitionComplete,
    onTransitionStart
  } = args;
  const { hasReducedMotion } = usePrefersReducedMotion();
  const setDurationToZero = shouldReduceMotion && hasReducedMotion;
  const [isMotionRunning, setIsMotionRunning] = React3.useState(false);
  const [previousState, setPreviousState] = React3.useState(currentState);
  const [previousAnimation, setPreviousAnimation] = React3.useState(
    null
  );
  useIsomorphicLayoutEffect(() => {
    if (!elementRef || elementRef.current === null) {
      return;
    }
    const animation = states.get(currentState);
    if (animation && animation !== previousAnimation) {
      if (SKIP_ANIMATIONS) {
        onTransitionStart && onTransitionStart({ currentState });
        onTransitionComplete && onTransitionComplete({ currentState });
        return;
      }
      setIsMotionRunning(true);
      setPreviousState(currentState);
      setPreviousAnimation(animation);
      onTransitionStart && onTransitionStart({ currentState });
      const cleanup = () => {
        setIsMotionRunning(false);
        onTransitionComplete && onTransitionComplete({ currentState });
      };
      try {
        animate(
          elementRef.current,
          animation.keyFrames,
          setDurationToZero ? { ...animation.options, duration: 0 } : animation.options
        ).then(() => {
          cleanup();
        });
      } catch (e) {
        cleanup();
        if (e.name === "InvalidStateError") {
          return;
        } else {
          weakInvariant(false, "Error in dig-components/useMotion:", e);
        }
      }
    }
  }, [
    elementRef,
    currentState,
    onTransitionStart,
    onTransitionComplete,
    setDurationToZero,
    states,
    previousState,
    previousAnimation
  ]);
  if (SKIP_ANIMATIONS) {
    return { isMotionRunning: false };
  }
  const hasStateChangeTriggeredAnAnimation = (
    // State has changed, we need to mount children before running an animation
    currentState !== previousState && // Here we need to make sure the state change _actually_ triggers an animation
    states.has(currentState)
  );
  return { isMotionRunning: isMotionRunning || hasStateChangeTriggeredAnAnimation };
}
export {
  EASE_ENTER,
  EASE_LEAVE,
  EASE_TRANSITION,
  MOTION_DURATION_1000,
  MOTION_DURATION_125,
  MOTION_DURATION_333,
  MOTION_DURATION_708,
  MOTION_DURATION_MICRO,
  MOTION_DURATION_MICRO_FAST,
  MOTION_DURATION_NON_MOTION,
  MOTION_DURATION_SURFACE,
  MOTION_DURATION_SURFACE_SLOW,
  Motion,
  SKIP_ANIMATIONS,
  useMotion
};
